import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BillingApiService, Commitment, PricingApiService, UpsertWholesalePricingRequest } from '@vendasta/billing';
import { BillingStrategy, billingStrategyToAPI } from './billing-strategy';
import { PricingPlanProductPricing, pricingPlanProductPricingToApi } from './pricing-plan-product';
import { pricesFromApi, ProductPrices, PurchaseCost, purchaseCostFromAPI } from './product-pricing';

@Injectable({ providedIn: 'root' })
export class WholesalePricingService {
  constructor(private pricingService: PricingApiService, private billingApi: BillingApiService) {}

  setProductBillingStrategy(merchantId: string, sku: string, billingStrategy: BillingStrategy): Observable<null> {
    const strategy = billingStrategyToAPI(billingStrategy);
    return this.pricingService.setWholesaleProductStrategy({ merchantId, sku, strategy }).pipe(map(() => null));
  }

  setProductVolumeCommitment(
    merchantId: string,
    sku: string,
    volumeCommitment: number,
    volumeCommitmentStartsAt?: Date,
    volumeCommitmentEndsAt?: Date,
  ): Observable<null> {
    return this.pricingService
      .setWholesaleProductVolumeCommitment({
        merchantId,
        sku,
        volumeCommitment,
        volumeCommitmentStartsAt: volumeCommitmentStartsAt,
        volumeCommitmentEndsAt: volumeCommitmentEndsAt,
      })
      .pipe(map(() => null));
  }

  getPurchaseCost(merchantId: string, sku: string, quantity: number): Observable<PurchaseCost> {
    return this.pricingService
      .getPurchaseCost({ merchantId, sku, quantity })
      .pipe(map((resp) => purchaseCostFromAPI(resp)));
  }

  getMultiWholesalePrices(merchantId: string, skus: string[], pricingPlanId?: string): Observable<ProductPrices[]> {
    return this.billingApi
      .getMultiProductPricing({ merchantId, skus, pricingPlanId })
      .pipe(map((r) => pricesFromApi(r.prices)));
  }

  upsertPricing(
    pricingPlanId: string,
    sku: string,
    pricing: PricingPlanProductPricing[],
    termCommitment: Commitment,
    strategy: BillingStrategy,
    volumeCommitment: number,
    volumeCommitmentStartsAt?: Date,
    volumeCommitmentEndsAt?: Date,
  ): Observable<HttpResponse<null>> {
    const request = new UpsertWholesalePricingRequest({
      pricingPlanId,
      sku,
      pricing: (pricing || []).map((p) => pricingPlanProductPricingToApi(p)),
      strategy: billingStrategyToAPI(strategy),
      termCommitment,
      volumeCommitment,
      volumeCommitmentStartsAt,
      volumeCommitmentEndsAt,
    });
    return this.pricingService.upsertWholesale(request);
  }
}
